import { useState, useEffect } from 'react';
import { Star, MessageSquare, ChevronDown, Pencil } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { StarRating } from './StarRating';
import { ReviewCard } from './ReviewCard';
import { WriteReviewModal } from './WriteReviewModal';
import { AuthModal } from './AuthModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductReviews = ({ productId, productName }) => {
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    average_rating: 0,
    total_reviews: 0,
    rating_breakdown: { 5: 0, 4: 0, 3: 0, 2: 0, 1: 0 }
  });
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('latest');
  const [showWriteModal, setShowWriteModal] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [canReview, setCanReview] = useState(false);
  const [reviewStatus, setReviewStatus] = useState({
    has_purchased: false,
    has_reviewed: false
  });
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  useEffect(() => {
    fetchReviews();
    checkCanReview();
  }, [productId, sortBy]);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${productId}/reviews?sort=${sortBy}`);
      setReviews(response.data.reviews);
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch reviews:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkCanReview = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoggedIn(false);
      return;
    }
    
    setIsLoggedIn(true);
    try {
      const response = await axios.get(`${API}/products/${productId}/can-review`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCanReview(response.data.can_review);
      setReviewStatus({
        has_purchased: response.data.has_purchased,
        has_reviewed: response.data.has_reviewed
      });
    } catch (error) {
      console.error('Failed to check review eligibility:', error);
    }
  };

  const handleReviewSubmitted = () => {
    fetchReviews();
    checkCanReview();
  };

  const handleAuthSuccess = () => {
    setIsLoggedIn(true);
    checkCanReview();
  };

  const getReviewButtonText = () => {
    if (!isLoggedIn) return 'Login to Write a Review';
    if (reviewStatus.has_reviewed) return 'Already Reviewed';
    if (!reviewStatus.has_purchased) return 'Purchase to Review';
    return 'Write a Review';
  };

  const handleWriteReviewClick = () => {
    if (!isLoggedIn) {
      setShowAuthModal(true);
      return;
    }
    if (reviewStatus.has_reviewed) {
      toast.info('You have already reviewed this product');
      return;
    }
    if (!reviewStatus.has_purchased) {
      toast.info('You can only review products you have purchased');
      return;
    }
    setShowWriteModal(true);
  };

  const getPercentage = (count) => {
    if (stats.total_reviews === 0) return 0;
    return Math.round((count / stats.total_reviews) * 100);
  };

  return (
    <div className="bg-[ivory] py-12" data-testid="product-reviews-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-[charcoal]">Customer Reviews</h2>
            <p className="text-[charcoal-light] mt-1">
              {stats.total_reviews > 0 
                ? `Based on ${stats.total_reviews} review${stats.total_reviews !== 1 ? 's' : ''}`
                : 'No reviews yet. Be the first to review!'}
            </p>
          </div>
          
          {/* Write Review Button */}
          <button
            onClick={handleWriteReviewClick}
            disabled={!canReview && isLoggedIn && reviewStatus.has_purchased}
            className={`inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold transition-all ${
              canReview
                ? 'bg-[charcoal] text-white hover:bg-[#2A2A2A]'
                : 'bg-gray-100 text-[charcoal-light] cursor-not-allowed'
            }`}
            data-testid="write-review-btn"
          >
            <Pencil className="w-4 h-4" />
            {getReviewButtonText()}
          </button>
        </div>

        {/* Stats and Reviews Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Left: Rating Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 sticky top-24">
              {/* Average Rating */}
              <div className="text-center mb-6">
                <div className="text-5xl font-bold text-[charcoal] mb-2">
                  {stats.average_rating.toFixed(1)}
                </div>
                <StarRating rating={Math.round(stats.average_rating)} size="lg" />
                <p className="text-sm text-[charcoal-light] mt-2">
                  {stats.total_reviews} review{stats.total_reviews !== 1 ? 's' : ''}
                </p>
              </div>

              {/* Rating Breakdown */}
              <div className="space-y-3">
                {[5, 4, 3, 2, 1].map((star) => (
                  <div key={star} className="flex items-center gap-3">
                    <span className="text-sm text-[charcoal-light] w-8">{star} ★</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-[gold] rounded-full transition-all duration-500"
                        style={{ width: `${getPercentage(stats.rating_breakdown[star])}%` }}
                      />
                    </div>
                    <span className="text-sm text-[charcoal-light] w-8 text-right">
                      {stats.rating_breakdown[star]}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Reviews List */}
          <div className="lg:col-span-3">
            {/* Sort Dropdown */}
            {reviews.length > 0 && (
              <div className="flex justify-end mb-4">
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="appearance-none bg-white border border-gray-200 rounded-lg px-4 py-2 pr-10 text-sm text-[charcoal] focus:outline-none focus:ring-2 focus:ring-[gold]"
                    data-testid="sort-reviews-select"
                  >
                    <option value="latest">Most Recent</option>
                    <option value="highest">Highest Rating</option>
                    <option value="lowest">Lowest Rating</option>
                  </select>
                  <ChevronDown className="w-4 h-4 text-[charcoal-light] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            )}

            {/* Reviews */}
            {loading ? (
              <div className="space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="bg-white rounded-xl p-5 animate-pulse">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full" />
                      <div className="space-y-2">
                        <div className="h-4 bg-gray-200 rounded w-24" />
                        <div className="h-3 bg-gray-200 rounded w-16" />
                      </div>
                    </div>
                    <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-4 bg-gray-200 rounded w-1/2" />
                  </div>
                ))}
              </div>
            ) : reviews.length === 0 ? (
              <div className="bg-white rounded-2xl p-12 text-center border border-gray-100">
                <MessageSquare className="w-16 h-16 text-[gold] mx-auto mb-4 opacity-50" />
                <h3 className="text-xl font-semibold text-[charcoal] mb-2">No Reviews Yet</h3>
                <p className="text-[charcoal-light] mb-6">
                  Be the first to share your experience with this product.
                </p>
                {canReview && (
                  <button
                    onClick={() => setShowWriteModal(true)}
                    className="inline-flex items-center gap-2 bg-[charcoal] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#2A2A2A] transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Write the First Review
                  </button>
                )}
              </div>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <ReviewCard key={review.id} review={review} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Write Review Modal */}
      <WriteReviewModal
        isOpen={showWriteModal}
        onClose={() => setShowWriteModal(false)}
        productId={productId}
        productName={productName}
        onReviewSubmitted={handleReviewSubmitted}
      />

      {/* Auth Modal for Login */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onAuthSuccess={handleAuthSuccess}
      />
    </div>
  );
};

export default ProductReviews;
